import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import IndexApp from "../../Components/IndexApp";
import { SETTINGS_NAV_INDEX } from "../../Components/AppBar/AppBarConfigs";
import { SettingsStyleContext } from "../../Components/Settings";
import { DV2 } from "../DeclarationV2/theme";
import { useDv2Theme } from "../DeclarationV2/layout/Ui";
import ClientProfile from "../Settings/StepSettings/ClientProfile";
import ClientAiciStep from "../Settings/StepSettings/ClientAiciStep";
import { CLIENT_SETTINGS_TAB_PARAM, ClientSettingsTab, clientDocumentsPath, isClientSettingsTab } from "./clientPaths";

/** Anciens numéros d'étape (`location.state.step`) : 1 profil, 3 avance immédiate, 2 documents (page dédiée). */
const TAB_FROM_LEGACY_STEP: Record<number, ClientSettingsTab> = { 1: "profil", 3: "avance-immediate" };
const CLIENT_LEGACY_DOCUMENTS_STEP = 2;
const NAV_BAR = { index: SETTINGS_NAV_INDEX };

/**
 * Paramètres du client repensés (espace client, 2 novembre 2026) : le même
 * menu de gauche que les paramètres du prof, deux rubriques (profil, avance
 * immédiate) qui réutilisent les étapes existantes (`ClientProfile`,
 * `ClientAiciStep`) habillées en cartes par `SettingsStyleContext` « v2 ».
 * Un lien d'invitation du prof (`?contact=`) ouvre l'avance immédiate.
 * L'ancienne page (`ClientSettings`) reste dans le code.
 */
const ClientSettingsV2 = () => {
    const client = useSelector((state: any) => state?.user?.currentUser?.client);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { primary, fontFamily, text } = useDv2Theme();

    const hasContactKey = searchParams.has("contact");
    const legacyStep: number | undefined = (location.state as any)?.step;
    const legacyTab = typeof legacyStep === "number" ? TAB_FROM_LEGACY_STEP[legacyStep] : undefined;
    const paramTab = searchParams.get(CLIENT_SETTINGS_TAB_PARAM);
    const tab: ClientSettingsTab = isClientSettingsTab(paramTab) ? paramTab : (hasContactKey ? "avance-immediate" : (legacyTab ?? "profil"));

    useEffect(() => {
        if (!isClientSettingsTab(paramTab) && (legacyTab || hasContactKey)) {
            const next = new URLSearchParams(searchParams);
            next.set(CLIENT_SETTINGS_TAB_PARAM, tab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectTab = (next: ClientSettingsTab) => {
        const params = new URLSearchParams(searchParams);
        params.set(CLIENT_SETTINGS_TAB_PARAM, next);
        setSearchParams(params, { replace: true });
    };

    if (legacyStep === CLIENT_LEGACY_DOCUMENTS_STEP) {
        return <Navigate to={clientDocumentsPath()} state={location.state} replace />;
    }

    const aiciActive = !!client?.idClientTiersPrestation;
    const tabs: Array<{ id: ClientSettingsTab; title: string; subtitle: string }> = [
        { id: "profil", title: "Profil", subtitle: "Identité, adresse et notifications" },
        { id: "avance-immediate", title: "Avance immédiate", subtitle: aiciActive ? "Compte URSSAF relié" : "À activer" },
    ];

    return (
        <IndexApp
            navBar={NAV_BAR}
            component={
                <SettingsStyleContext.Provider value="v2">
                    <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                        <div className="mx-auto w-full px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                            <header>
                                <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Paramètres</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>Votre profil et votre avance immédiate</div>
                            </header>
                            <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6 items-start">
                                <nav aria-label="Rubriques des paramètres" style={{ background: DV2.surface, border: `1px solid ${DV2.border}`, borderRadius: DV2.radius, padding: 10 }} className="lg:sticky lg:top-24">
                                    <div className="flex lg:flex-col gap-1 overflow-x-auto" role="tablist">
                                        {tabs.map((t) => {
                                            const selected = t.id === tab;
                                            return (
                                                <button
                                                    key={t.id}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={selected}
                                                    onClick={() => selectTab(t.id)}
                                                    className="text-left flex-shrink-0"
                                                    style={{ fontFamily, padding: "12px 14px", borderRadius: 12, border: "none", background: selected ? DV2.tint : "transparent", cursor: "pointer", minWidth: 150 }}
                                                >
                                                    <div style={{ fontWeight: 800, fontSize: 14, color: selected ? primary : text }}>{t.title}</div>
                                                    <div style={{ fontSize: 12, color: DV2.muted }}>{t.subtitle}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </nav>
                                <div className="min-w-0" key={tab}>
                                    {client?.id ? (tab === "avance-immediate" ? <ClientAiciStep /> : <ClientProfile client={client} />) : null}
                                </div>
                            </div>
                        </div>
                    </main>
                </SettingsStyleContext.Provider>
            }
        />
    );
};

export default ClientSettingsV2;
